import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Competition } from '@/types/domain';
import PickemLeaderboard from '@/components/leaderboard/PickemLeaderboard';
import LeaderboardSection from '@/components/leaderboard/LeaderboardSection';
import EmptyState from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';

export interface LeaderboardTabProps {
  competitions: Competition[];
  isLoading: boolean;
  pickemFullNavigation: boolean;
  currentUserId?: string;
}

const LeaderboardTab: React.FC<LeaderboardTabProps> = ({
  competitions,
  isLoading,
  pickemFullNavigation,
  currentUserId,
}) => {
  const { t } = useTranslation();
  const activePickem = useMemo(() => {
    const playable = competitions
      .filter(c => c.state !== 'final')
      .sort((a, b) => {
        if (a.isOpen !== b.isOpen) return a.isOpen ? -1 : 1;
        return a.gameDate.getTime() - b.gameDate.getTime();
      });
    return playable[0];
  }, [competitions]);

  if (isLoading) {
    return <LoadingSpinner message={t('leaderboard.loading')} />;
  }

  // Full-navigation tenants (pre-DKPLAT-347) show the aggregate global
  // leaderboard rather than the single most-relevant pickem board.
  if (pickemFullNavigation) {
    return <LeaderboardSection title={t('leaderboard.globalTitle')} />;
  }

  if (!activePickem) {
    return <EmptyState title={t('leaderboard.noActive')} showAdSpace={false} />;
  }

  return (
    <PickemLeaderboard
      pickemId={activePickem.id}
      isOpen={true}
      onClose={() => {}}
      currentUserId={currentUserId}
      contestStartUtc={activePickem.gameDate.toISOString()}
    />
  );
};

export default LeaderboardTab;
